import { LitElement, css, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { listCampaigns, deleteCampaign } from '../api/client';
import type { CampaignListItem, CampaignStatus, PaginatedCampaigns } from '../api/types';
import { ApiClientError } from '../api/types';
import '../components/ui/ui-button';
import '../components/ui/ui-modal';
import '../components/ui/ui-double-confirm';
import './campaign-create-form';
import { motionStyles, alertStyles, boxSizingStyles, responsiveStyles } from '../styles/lit-shared';
import { navigate } from '../router';

const CAMPAIGN_STATUS_CONFIG: Record<CampaignStatus, { label: string; class: string }> = {
  planning: { label: 'Diseñando plan…', class: 'badge--pending' },
  active: { label: 'Activa', class: 'badge--completed' },
  failed: { label: 'Error', class: 'badge--failed' },
};

function formatDate(iso: string): string {
  return new Intl.DateTimeFormat('es-ES', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }).format(new Date(iso));
}

@customElement('campaign-list-page')
export class CampaignListPage extends LitElement {
  static styles = [
    boxSizingStyles,
    responsiveStyles,
    motionStyles,
    alertStyles,
    css`
      :host { display: block; }

      .hero {
        position: relative;
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: var(--space-lg);
        flex-wrap: wrap;
        margin-bottom: var(--space-2xl);
        padding: var(--space-xl);
        border-radius: var(--radius-lg);
        background: var(--gradient-primary-soft);
        border: 1px solid var(--color-border-glow);
        overflow: hidden;
      }

      .hero::before {
        content: '';
        position: absolute;
        top: -50%;
        right: -5%;
        width: 260px;
        height: 260px;
        border-radius: 50%;
        background: var(--glow-violet);
        filter: blur(50px);
        pointer-events: none;
      }

      .hero-text {
        position: relative;
        z-index: 1;
      }

      .hero-eyebrow {
        display: inline-flex;
        align-items: center;
        gap: 0.375rem;
        margin-bottom: var(--space-sm);
        padding: 0.25rem 0.75rem;
        border-radius: var(--radius-full);
        background: rgb(255 255 255 / 6%);
        border: 1px solid var(--color-border);
        font-size: 0.75rem;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.06em;
        color: var(--color-accent);
      }

      h1 {
        font-size: clamp(1.5rem, 3.5vw, 2rem);
        font-weight: 700;
        letter-spacing: -0.03em;
        margin-bottom: var(--space-xs);
        background: var(--gradient-text);
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
      }

      .subtitle {
        color: var(--color-text-muted);
        font-size: 0.9375rem;
        max-width: 42ch;
      }

      .hero-action {
        position: relative;
        z-index: 1;
        display: flex;
        align-items: center;
        flex-shrink: 0;
      }

      .section-header {
        display: flex;
        align-items: center;
        gap: var(--space-sm);
        margin-bottom: var(--space-lg);
      }

      .section-header h2 {
        font-size: 1.125rem;
        font-weight: 700;
        letter-spacing: -0.02em;
        color: var(--color-text);
      }

      .section-header::after {
        content: '';
        flex: 1;
        height: 1px;
        background: linear-gradient(90deg, var(--color-border-strong) 0%, transparent 100%);
      }

      .campaign-list {
        display: flex;
        flex-direction: column;
        gap: var(--space-sm);
      }

      .campaign-card {
        position: relative;
        display: flex;
        align-items: center;
        flex-wrap: wrap;
        gap: var(--space-md);
        padding: var(--space-md) var(--space-lg);
        border-radius: var(--radius);
        background: var(--color-surface);
        border: 1px solid var(--color-border);
        backdrop-filter: blur(12px);
        -webkit-backdrop-filter: blur(12px);
        cursor: pointer;
        transition:
          border-color var(--duration-fast) var(--ease-out),
          transform var(--duration-fast) var(--ease-out),
          box-shadow var(--duration-fast) var(--ease-out);
        color: inherit;
      }

      .campaign-card::before {
        content: '';
        position: absolute;
        left: 0;
        top: 0;
        bottom: 0;
        width: 3px;
        border-radius: var(--radius) 0 0 var(--radius);
        background: var(--gradient-primary);
        opacity: 0.6;
      }

      .campaign-card:hover {
        border-color: var(--color-border-glow);
        transform: translateX(3px);
        box-shadow: var(--shadow-glow-primary);
      }

      .card-content {
        flex: 1;
        min-width: 0;
      }

      .card-title {
        font-size: 0.9375rem;
        font-weight: 600;
        color: var(--color-text);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        margin-bottom: 0.125rem;
      }

      .card-subtitle {
        font-size: 0.8125rem;
        color: var(--color-text-muted);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
        margin-bottom: 0.25rem;
      }

      .card-meta {
        display: flex;
        align-items: center;
        gap: var(--space-sm);
        flex-wrap: wrap;
        font-size: 0.8125rem;
        color: var(--color-text-muted);
      }

      .card-arrow {
        font-size: 1rem;
        color: var(--color-text-muted);
        flex-shrink: 0;
        transition: color var(--duration-fast) var(--ease-out);
      }

      .campaign-card:hover .card-arrow {
        color: var(--color-primary-light);
      }

      .card-delete-btn {
        flex-shrink: 0;
        opacity: 0;
        transition: opacity var(--duration-fast) var(--ease-out);
      }

      .campaign-card:hover .card-delete-btn,
      .card-delete-btn:focus-within {
        opacity: 1;
      }

      /* Badges */
      .badge {
        display: inline-flex;
        align-items: center;
        gap: 0.3rem;
        padding: 0.2rem 0.6rem;
        border-radius: var(--radius-full);
        font-size: 0.75rem;
        font-weight: 600;
        letter-spacing: 0.03em;
      }

      .badge-dot {
        width: 6px;
        height: 6px;
        border-radius: 50%;
        flex-shrink: 0;
      }

      .badge--pending {
        background: rgb(251 191 36 / 12%);
        color: var(--color-warning);
        border: 1px solid rgb(251 191 36 / 25%);
      }

      .badge--pending .badge-dot {
        background: var(--color-warning);
        animation: pulse-glow 2s ease-in-out infinite;
      }

      .badge--completed {
        background: rgb(52 211 153 / 12%);
        color: var(--color-success);
        border: 1px solid rgb(52 211 153 / 25%);
      }

      .badge--completed .badge-dot { background: var(--color-success); }

      .badge--failed {
        background: rgb(251 113 133 / 12%);
        color: var(--color-error);
        border: 1px solid rgb(251 113 133 / 25%);
      }

      .badge--failed .badge-dot { background: var(--color-error); }

      /* Pagination */
      .pagination {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: var(--space-md);
        margin-top: var(--space-xl);
      }

      .page-info {
        font-size: 0.875rem;
        color: var(--color-text-muted);
      }

      /* States */
      .empty-state,
      .loading-state,
      .error-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: var(--space-md);
        padding: var(--space-2xl) 0;
        text-align: center;
        color: var(--color-text-muted);
      }

      .empty-icon {
        font-size: 2.5rem;
        opacity: 0.5;
      }

      .error-state {
        padding: var(--space-lg);
        border-radius: var(--radius);
        background: rgb(244 63 94 / 8%);
        border: 1px solid rgb(251 113 133 / 25%);
        color: var(--color-error);
      }

      @media (max-width: 639px) {
        .campaign-card {
          padding: var(--space-md);
        }

        .campaign-card:hover {
          transform: none;
        }

        .card-delete-btn {
          opacity: 1;
        }

        .pagination {
          flex-wrap: wrap;
          gap: var(--space-sm);
        }
      }
    `,
  ];

  @state() private data: PaginatedCampaigns | null = null;
  @state() private loading = true;
  @state() private error = '';
  @state() private page = 1;
  @state() private modalOpen = false;
  @state() private confirmOpen = false;
  @state() private pendingDeleteId = '';
  @state() private pendingDeleteTitle = '';
  @state() private deleting = false;

  connectedCallback() {
    super.connectedCallback();
    void this.load();
  }

  private async load() {
    this.loading = true;
    this.error = '';
    try {
      this.data = await listCampaigns(this.page);
    } catch (err) {
      this.error =
        err instanceof ApiClientError
          ? err.message
          : 'Error al cargar las campañas.';
    } finally {
      this.loading = false;
    }
  }

  private async goToPage(p: number) {
    this.page = p;
    await this.load();
  }

  private openModal() { this.modalOpen = true; }
  private closeModal() { this.modalOpen = false; }

  private async handleCampaignCreated(e: CustomEvent<{ campaignId: string }>) {
    this.closeModal();
    void navigate(`/campaigns/${e.detail.campaignId}`);
  }

  private openDeleteConfirm(item: CampaignListItem) {
    this.pendingDeleteId = item.id;
    this.pendingDeleteTitle = item.topic;
    this.confirmOpen = true;
  }

  private closeDeleteConfirm() {
    if (this.deleting) return;
    this.confirmOpen = false;
    this.pendingDeleteId = '';
    this.pendingDeleteTitle = '';
  }

  private async handleDeleteConfirm() {
    if (!this.pendingDeleteId) return;
    this.deleting = true;
    try {
      await deleteCampaign(this.pendingDeleteId);
      this.confirmOpen = false;
      this.pendingDeleteId = '';
      this.pendingDeleteTitle = '';
      await this.load();
    } catch (err) {
      const msg =
        err instanceof ApiClientError
          ? err.message
          : 'Error al eliminar la campaña.';
      alert(msg);
    } finally {
      this.deleting = false;
    }
  }

  private renderBadge(status: CampaignStatus) {
    const cfg = CAMPAIGN_STATUS_CONFIG[status] ?? CAMPAIGN_STATUS_CONFIG.active;
    return html`
      <span class="badge ${cfg.class}">
        <span class="badge-dot"></span>
        ${cfg.label}
      </span>
    `;
  }

  private renderCard(item: CampaignListItem) {
    const topic = item.topic.length > 70 ? item.topic.slice(0, 67) + '…' : item.topic;
    const target = item.targetAudience.length > 80
      ? item.targetAudience.slice(0, 77) + '…'
      : item.targetAudience;

    return html`
      <div
        class="campaign-card"
        role="link"
        tabindex="0"
        @click=${() => void navigate(`/campaigns/${item.id}`)}
        @keydown=${(e: KeyboardEvent) => {
          if (e.key === 'Enter') void navigate(`/campaigns/${item.id}`);
        }}
      >
        <div class="card-content">
          <p class="card-title" title=${item.topic}>${topic}</p>
          <p class="card-subtitle" title=${item.targetAudience}>${target}</p>
          <div class="card-meta">
            ${this.renderBadge(item.status)}
            <span>${formatDate(item.createdAt)}</span>
          </div>
        </div>
        <span
          class="card-delete-btn"
          @click=${(e: Event) => e.stopPropagation()}
        >
          <ui-button
            variant="ghost"
            aria-label="Eliminar campaña"
            @ui-click=${() => this.openDeleteConfirm(item)}
          >Eliminar</ui-button>
        </span>
        <span class="card-arrow" aria-hidden="true">→</span>
      </div>
    `;
  }

  render() {
    return html`
      <div class="animate-fade-up">
        <section class="hero">
          <div class="hero-text">
            <span class="hero-eyebrow">◈ Campañas</span>
            <h1>Campañas</h1>
            <p class="subtitle">
              Secuencias de 10 posts con evolución narrativa y tono canalla diseñadas por Blunticle para convertir.
            </p>
          </div>
          <div class="hero-action">
            <ui-button @ui-click=${this.openModal}>
              + Nueva campaña
            </ui-button>
          </div>
        </section>

        ${this.loading
          ? html`
            <div class="loading-state">
              <div class="spinner" role="status" aria-label="Cargando"></div>
              <p>Cargando campañas…</p>
            </div>
          `
          : this.error
            ? html`<div class="error-state">${this.error}</div>`
            : this.data && this.data.items.length === 0
              ? html`
                <div class="empty-state">
                  <span class="empty-icon" aria-hidden="true">◈</span>
                  <p>Aún no tienes campañas. ¡Crea la primera!</p>
                </div>
              `
              : html`
                <div class="section-header">
                  <h2>Campañas</h2>
                </div>
                <div class="campaign-list">
                  ${this.data!.items.map((item) => this.renderCard(item))}
                </div>
                ${this.data!.pagination.totalPages > 1
                  ? html`
                    <div class="pagination">
                      <ui-button
                        variant="secondary"
                        ?disabled=${this.page <= 1}
                        @ui-click=${() => void this.goToPage(this.page - 1)}
                      >← Anterior</ui-button>
                      <span class="page-info">
                        ${this.page} / ${this.data!.pagination.totalPages}
                      </span>
                      <ui-button
                        variant="secondary"
                        ?disabled=${this.page >= this.data!.pagination.totalPages}
                        @ui-click=${() => void this.goToPage(this.page + 1)}
                      >Siguiente →</ui-button>
                    </div>
                  `
                  : ''}
              `}
      </div>

      <ui-modal
        .open=${this.modalOpen}
        title="Nueva campaña"
        @modal-close=${this.closeModal}
      >
        <campaign-create-form
          @campaign-created=${(e: CustomEvent<{ campaignId: string }>) =>
            void this.handleCampaignCreated(e)}
        ></campaign-create-form>
      </ui-modal>

      <ui-double-confirm
        .open=${this.confirmOpen}
        title="Eliminar campaña"
        step1-message=${`¿Eliminar la campaña "${this.pendingDeleteTitle}"?`}
        step2-message="Se eliminarán también todas las generaciones asociadas. Esta acción no se puede deshacer."
        confirm-label="Eliminar"
        .loading=${this.deleting}
        @modal-close=${this.closeDeleteConfirm}
        @confirm=${() => void this.handleDeleteConfirm()}
      ></ui-double-confirm>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'campaign-list-page': CampaignListPage;
  }
}
